import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table/data-table";
import { useDataTable } from "@/lib/hooks/use-data-table";
import { useGetMentorships } from "@/lib/api/dashboard";
import { mentorshipColumns } from "./columns";
import { AlertTriangle, Calendar, MessageCircle, Timer } from "lucide-react";
import { useState } from "react";
import { useRouter, useSearch } from "@tanstack/react-router";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useDashboardParams } from "@/lib/hooks/admin/use-dashboard-params";
import { useCurrentUser, UserRole } from "@/lib/api/user";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { useGetMyMentorships } from "@/lib/api/mentorships";
import { getDeviceTimezone } from "@/lib/utils/timezone";

// Get current timezone
const CURRENT_TIMEZONE = getDeviceTimezone();

type TabConfig = {
  key: string;
  label: string;
  engagementFilter?: "engaged" | "not_engaged";
  statusFilter?: string;
  defaultPageSize?: number;
};

const DEFAULT_TABS: TabConfig[] = [
  { key: "all", label: "All" },
  { key: "engaged", label: "Engaged", engagementFilter: "engaged" },
  {
    key: "below-threshold",
    label: "Below Threshold",
    engagementFilter: "not_engaged",
  },
  { key: "de-activated", label: "De-activated", statusFilter: "inactive" },
];

export function UserTable({
  selectedMonth,
  availableTabs = DEFAULT_TABS,
  showBadgeFilters = true,
  defaultPageSize = 10,
  mentorId: mentorIdProp,
}: {
  selectedMonth: number;
  availableTabs?: TabConfig[];
  showBadgeFilters?: boolean;
  defaultPageSize?: number;
  mentorId?: string;
}) {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>(() => {
    // Default to "all" if it exists in availableTabs, otherwise use the first tab
    const hasAllTab = availableTabs.some((tab) => tab.key === "all");
    return hasAllTab ? "all" : availableTabs[0]?.key || "all";
  });

  const router = useRouter();
  const mentorView = useMentorView((state) => state.mentorView);
  const setCurrentConversation = useCurrentConversation(
    (state) => state.setCurrentConversation,
  );

  // Get mentorId from search params
  const { mentorId } = useSearch({ strict: false }) as { mentorId?: string };
  const rawSearchParams = useSearch({ strict: false }) as Record<
    string,
    string
  >;
  const { data: currentUser } = useCurrentUser();

  const isMentor = currentUser?.role === UserRole.MENTOR;

  // Fetch mentor's own mentorships to get sdk_url for conversation navigation.
  // Only enabled for actual mentors — admins in mentor view would get their own
  // mentorships (wrong data), so we skip the query for them.
  const { data: myMentorships } = useGetMyMentorships({ enabled: isMentor });

  const targetMentorId = mentorIdProp || mentorId || currentUser?.id || "";

  // Use the same dashboard params hook as admin page
  const { page, per_page } = useDashboardParams();
  const currentPage = page;
  const pageSize = rawSearchParams.per_page ? per_page : defaultPageSize;

  const toggleFilter = (filter: string) => {
    setSelectedFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter],
    );
  };

  // Map activeTab to backend engagementFilter
  const getEngagementFilter = (
    tab: string,
  ): "engaged" | "not_engaged" | undefined => {
    const tabConfig = availableTabs.find((t) => t.key === tab);
    return tabConfig?.engagementFilter;
  };

  // Map activeTab to backend status filter
  const getStatusFilter = (tab: string): string | undefined => {
    const tabConfig = availableTabs.find((t) => t.key === tab);
    return tabConfig?.statusFilter;
  };

  const { data: mentorshipsData, isPending: mentorshipsLoading } =
    useGetMentorships({
      month: selectedMonth,
      page: currentPage,
      perPage: pageSize,
      mentorId: targetMentorId,
      engagementFilter: getEngagementFilter(activeTab),
      status: getStatusFilter(activeTab),
      deactivatedThisMonth: activeTab === "de-activated",
      isMentor: true,
      timezone: CURRENT_TIMEZONE,
    });

  // Filter mentorships based on selectedFilters (frontend filtering for badges)
  const filteredMentorships = (mentorshipsData?.mentorships || []).filter(
    (mentorship) => {
      // If no filters selected, show all
      if (selectedFilters.length === 0) return true;

      // Check if mentorship matches ALL selected filters
      return selectedFilters.every((filter) => {
        switch (filter) {
          case "new-month":
            // New mentorships have fewer days in mentorship
            return (
              new Date(mentorship.created_at).getMonth() + 1 === selectedMonth
            );
          case "at-risk":
            // At risk mentorships have low days to churn
            return (
              mentorship.days_to_churn <= 7 && mentorship.days_to_churn > 0
            );
          case "highly-engaged":
            // Highly engaged have many messages and good engagement
            return parseInt(mentorship.teen_messages || "0") >= 85;
          case "escalated":
            // Escalated mentorships have open escalations
            return mentorship.has_open_escalation === true;
          default:
            return true;
        }
      });
    },
  );

  // Initialize the data table exactly like admin page
  const { table } = useDataTable({
    columns: mentorshipColumns,
    data: filteredMentorships,
    defaultPerPage: pageSize,
    pageCount: -1, // Server-side pagination
    filterFields: [],
  });

  // Pagination handlers exactly like admin page
  const handlePageChange = (newPageIndex: number) => {
    const newPage = newPageIndex + 1;
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: newPage,
      }),
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: 1, // Reset to first page when changing page size
        per_page: newPageSize,
      }),
    });
  };

  return (
    <Card className="rounded-none border-0">
      <CardHeader>
        <div className="flex flex-col gap-4">
          {/* Filter Tabs */}
          <div className="flex gap-6 border-b border-gray-200">
            {availableTabs.map((tab) => (
              <button
                key={tab.key}
                className={`font-['TWK Lausanne'] text-md pb-2 font-medium ${
                  activeTab === tab.key
                    ? "border-b-2 border-gray-900 font-medium text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filter Badges */}
          {showBadgeFilters && (
            <div className="flex flex-wrap gap-2">
              <Badge
                variant="outline"
                className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs hover:cursor-pointer hover:border-gray-200 hover:bg-gray-50 hover:text-gray-800 ${
                  selectedFilters.includes("new-month")
                    ? "border-gray-300 bg-gray-100 text-gray-900"
                    : "text-gray-500"
                }`}
                onClick={() => toggleFilter("new-month")}
              >
                <Calendar className="h-3 w-3" />
                New this month
              </Badge>
              <Badge
                variant="outline"
                className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs hover:cursor-pointer hover:border-gray-200 hover:bg-gray-50 hover:text-gray-800 ${
                  selectedFilters.includes("at-risk")
                    ? "border-gray-300 bg-gray-100 text-gray-900"
                    : "text-gray-500"
                }`}
                onClick={() => toggleFilter("at-risk")}
              >
                <Timer className="h-3 w-3" />
                At risk
              </Badge>
              <Badge
                variant="outline"
                className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs hover:cursor-pointer hover:border-gray-200 hover:bg-gray-50 hover:text-gray-800 ${
                  selectedFilters.includes("highly-engaged")
                    ? "border-gray-300 bg-gray-100 text-gray-900"
                    : "text-gray-500"
                }`}
                onClick={() => toggleFilter("highly-engaged")}
              >
                <MessageCircle className="h-3 w-3" />
                Highly engaged
              </Badge>
              <Badge
                variant="outline"
                className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs hover:cursor-pointer hover:border-gray-200 hover:bg-gray-50 hover:text-gray-800 ${
                  selectedFilters.includes("escalated")
                    ? "border-gray-300 bg-gray-100 text-gray-900"
                    : "text-gray-500"
                }`}
                onClick={() => toggleFilter("escalated")}
              >
                <AlertTriangle className="h-3 w-3" />
                Escalated
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <DataTable
          table={table}
          isPending={mentorshipsLoading}
          tableContainerClassName="border-0 rounded-none"
          getCanNextPage={() => {
            return mentorshipsData?.hasMore || false;
          }}
          getCanPreviousPage={() => {
            return currentPage > 1;
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onRowClick={(row) => {
            if (isMentor || mentorView) {
              // For mentors (or admins in mentor view), navigate to conversations.
              // For actual mentors, pre-select the conversation channel.
              if (isMentor) {
                const matchedMentorship = myMentorships?.find(
                  (m) => m.id === row.mentorship_id,
                );
                if (matchedMentorship?.sdk_url) {
                  setCurrentConversation(matchedMentorship.sdk_url);
                }
              }
              router.navigate({
                to: `/conversations`,
              });
            } else {
              router.navigate({
                to: `/mentorships/${row.mentorship_id}`,
              });
            }
          }}
        />
      </CardContent>
    </Card>
  );
}
